/**
 * A number, and the records behind it.
 *
 * ── The request ────────────────────────────────────────────────────────────
 *
 * Kyu, 14 September, pointing at the Pipeline: "Arrived four, emailed two. I
 * need to know who are those two and who are those four." Then, on the second
 * pass, the sentence that decided the design: it should open a page, not a box.
 *
 * ── Why this file is twenty lines and not a component ──────────────────────
 *
 * Because the page already exists. The Leads tab lists leads — searching,
 * sorting, grouping, bulk selection, click through to the lead panel. The
 * Companies tab lists companies. A drill-down dialog would have been a second,
 * worse table sitting beside four good ones.
 *
 * So a number does not open anything. It hands over a Drill, the page it names
 * applies the ids as a filter, and everything that page could already do keeps
 * working on the narrowed set.
 *
 * ── The rule that makes this safe ──────────────────────────────────────────
 *
 * `ids` must come from the same computation as the number. Not a second filter
 * that agrees today. A drill that says four and lists three is worse than no
 * drill at all, because the first one is a question and the second is a lie,
 * and `drillCount` plus the table-driven test in drill.test.ts exist to keep
 * that honest.
 */

/** The tab a drill lands on, and the filter field the ids go into. */
export type DrillKind = 'lead' | 'company' | 'deal' | 'task' | 'prospect';

export interface Drill {
  /**
   * What was clicked, in the words that were on screen, plus the slice it was
   * taken from. "Emailed" alone is ambiguous the moment somebody changes the
   * range control; "Emailed · Outbound · last week" is not.
   */
  label: string;
  /**
   * Why the number is what it is, verbatim from wherever that sentence already
   * lives — FUNNEL_EXPLAINERS and the metric hints. Empty when the number has
   * no written definition yet, which is itself worth seeing.
   *
   * The dialog design put this in the dialog. The banner inherits the job,
   * because "why is this two and not three" is asked at exactly the moment
   * somebody drills in.
   */
  why: string;
  kind: DrillKind;
  /**
   * The records. An EMPTY array is a real answer meaning nothing matched; it
   * is not the same as no drill. A number reading zero opens an empty table.
   */
  ids: readonly string[];
}

/** What a drill claims to hold. Must equal the number that was clicked. */
export function drillCount(drill: Drill): number {
  return drill.ids.length;
}

/**
 * Distinct ids, in first-seen order.
 *
 * Not tidiness. Several numbers are built by walking activity rows rather than
 * leads — two touches to one lead is two rows and one lead — so a builder that
 * collects as it walks produces duplicates, and the count would then disagree
 * with the count on screen in the one direction nobody checks. Builders call
 * this; the assertion in drill.test.ts checks they did.
 */
export function uniqueIds(ids: Iterable<string>): string[] {
  return [...new Set(ids)];
}

/** A drill, with its ids deduplicated. The only constructor worth using. */
export function drillOf(input: Drill): Drill {
  return { ...input, ids: uniqueIds(input.ids) };
}

/**
 * The label a banner shows, with the slice appended when there is one.
 *
 * Kept here rather than at each call site so every number phrases it the same
 * way, and so the separator is decided once.
 */
export function drillLabel(name: string, slice: string): string {
  const trimmed = slice.trim();
  return trimmed === '' ? name : `${name} · ${trimmed}`;
}
